/**
 * Payment Method Selector Component
 * Allows users to select payment method (Card, Apple Pay, Google Pay, ACH)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CreditCard, Smartphone, Building2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { SavedPaymentMethods } from './SavedPaymentMethods';
import { GooglePayButton } from './GooglePayButton';
import { ApplePayButton } from './ApplePayButton';

interface PaymentMethodOption {
  id: string;
  type: 'card' | 'apple_pay' | 'google_pay' | 'ach' | 'coinflow_iframe';
  displayName: string;
  icon: React.ReactNode;
  description: string;
  processors: string[]; // Available processors for this method
}

interface PaymentMethodSelectorProps {
  onSelect: (method: PaymentMethodOption['type'], processorId: string) => void;
  onSelectSavedCard?: (savedCard: any) => void; // Callback for when a saved card is selected
  packageId: string;
  scheduledOfferId?: string;  // For scheduled offer purchases
  amount: number;
  refreshKey?: number; // Key to trigger refresh of saved cards
  packageTitle?: string; // Package title for Google Pay and Apple Pay
  onGooglePaySuccess?: (paymentId: string) => void; // Callback for Google Pay success
  onGooglePayError?: (error: string | Error) => void; // Callback for Google Pay error
  onApplePaySuccess?: (paymentId: string) => void; // Callback for Apple Pay success
  onApplePayError?: (error: string | Error) => void; // Callback for Apple Pay error
  /** Checkout session transaction ID - shared across all payment methods */
  checkoutSessionId?: number | null;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  onSelect,
  onSelectSavedCard,
  packageId,
  scheduledOfferId,
  amount,
  refreshKey = 0,
  packageTitle = 'Package',
  onGooglePaySuccess,
  onGooglePayError,
  onApplePaySuccess,
  onApplePayError,
  checkoutSessionId,
}) => {
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasSavedCards, setHasSavedCards] = useState(false);
  const [isGooglePayMethodAvailable, setIsGooglePayMethodAvailable] = useState(false);
  const [isApplePayMethodAvailable, setIsApplePayMethodAvailable] = useState(false);

  useEffect(() => {
    // Check Apple Pay availability
    const checkApplePay = () => {
      if (typeof window !== 'undefined' && (window as any).ApplePaySession) {
        try {
          const canMakePayments = (window as any).ApplePaySession.canMakePayments();
          setIsApplePayAvailable(canMakePayments);
        } catch (error) {
          setIsApplePayAvailable(false);
        }
      } else {
        setIsApplePayAvailable(false);
      }
    };

    // Check Google Pay availability (load script if needed)
    const checkGooglePay = () => {
      if (typeof window === 'undefined') {
        setIsGooglePayAvailable(false);
        return;
      }

      // Check if already loaded
      if ((window as any).google?.payments?.api?.PaymentsClient) {
        // Google Pay is loaded, check if ready
        try {
          const client = new (window as any).google.payments.api.PaymentsClient({
            environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
          });

          client
            .isReadyToPay({
              apiVersion: 2,
              apiVersionMinor: 0,
              allowedPaymentMethods: [
                {
                  type: 'CARD',
                  parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: ['VISA', 'MASTERCARD'],
                  },
                },
              ],
            })
            .then((response: any) => {
              setIsGooglePayAvailable(response.result || false);
            })
            .catch(() => {
              setIsGooglePayAvailable(false);
            });
        } catch (error) {
          setIsGooglePayAvailable(false);
        }
      } else {
        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src="https://pay.google.com/gp/p/pay.js"]');
        if (existingScript) {
          // Script is already loading, wait for it
          existingScript.addEventListener('load', () => {
            if ((window as any).google?.payments?.api?.PaymentsClient) {
              try {
                const client = new (window as any).google.payments.api.PaymentsClient({
                  environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
                });

                client
                  .isReadyToPay({
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                      {
                        type: 'CARD',
                        parameters: {
                          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                          allowedCardNetworks: ['VISA', 'MASTERCARD'],
                        },
                      },
                    ],
                  })
                  .then((response: any) => {
                    setIsGooglePayAvailable(response.result || false);
                  })
                  .catch(() => {
                    setIsGooglePayAvailable(false);
                  });
              } catch (error) {
                setIsGooglePayAvailable(false);
              }
            } else {
              setIsGooglePayAvailable(false);
            }
          });
          return;
        }

        // Load Google Pay script
        const script = document.createElement('script');
        script.src = 'https://pay.google.com/gp/p/pay.js';
        script.async = true;
        script.onload = () => {
          if ((window as any).google?.payments?.api?.PaymentsClient) {
            try {
              const client = new (window as any).google.payments.api.PaymentsClient({
                environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
              });

              client
                .isReadyToPay({
                  apiVersion: 2,
                  apiVersionMinor: 0,
                  allowedPaymentMethods: [
                    {
                      type: 'CARD',
                      parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['VISA', 'MASTERCARD'],
                      },
                    },
                  ],
                })
                .then((response: any) => {
                  setIsGooglePayAvailable(response.result || false);
                })
                .catch(() => {
                  setIsGooglePayAvailable(false);
                });
            } catch (error) {
              setIsGooglePayAvailable(false);
            }
          } else {
            setIsGooglePayAvailable(false);
          }
        };
        script.onerror = () => {
          setIsGooglePayAvailable(false);
        };
        document.head.appendChild(script);
      }
    };

    // Check Apple Pay first (synchronous)
    checkApplePay();

    // Check Google Pay (may be async)
    checkGooglePay();
  }, []);

  // Refetch methods whenever availability changes
  useEffect(() => {
    fetchAvailableMethods(isApplePayAvailable, isGooglePayAvailable);
  }, [isApplePayAvailable, isGooglePayAvailable]);

  const fetchAvailableMethods = async (applePayAvailable: boolean, googlePayAvailable: boolean) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/payments/available-methods');
      const availableMethodsData = response.methods || [];
      const methodProcessors = response.methodProcessors || {};

      // Build method options from available methods
      const availableMethods: PaymentMethodOption[] = [];

      // Card
      if (availableMethodsData.includes('card')) {
        availableMethods.push({
          id: 'card',
          type: 'card',
          displayName: 'Credit or Debit Card',
          icon: <CreditCard className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Visa, Mastercard',
          processors: methodProcessors['card'] || [],
        });
      }

      // Apple Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list

      // Google Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list
      setIsGooglePayMethodAvailable(availableMethodsData.includes('google_pay'));
      
      // Apple Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list
      setIsApplePayMethodAvailable(availableMethodsData.includes('apple_pay'));

      // ACH
      if (availableMethodsData.includes('ach')) {
        availableMethods.push({
          id: 'ach',
          type: 'ach',
          displayName: 'Bank Transfer (ACH)',
          icon: <Building2 className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Lower fees, 2-3 business days',
          processors: methodProcessors['ach'] || [],
        });
      }

      // Coinflow Hosted Checkout - now driven by API (admin-configurable)
      // Shows when coinflow_hosted method is enabled in payment processor config
      if (availableMethodsData.includes('coinflow_hosted')) {
        availableMethods.push({
          id: 'coinflow_iframe',
          type: 'coinflow_iframe',
          displayName: 'Coinflow Checkout',
          icon: <ExternalLink className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Hosted secure payment page',
          processors: methodProcessors['coinflow_hosted'] || ['coinflow'],
        });
      }

      setMethods(availableMethods);
    } catch {
      // Fallback to default methods if API fails
      setMethods([
        {
          id: 'card',
          type: 'card',
          displayName: 'Credit or Debit Card',
          icon: <CreditCard className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Visa, Mastercard',
          processors: ['coinflow'],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodSelect = (method: PaymentMethodOption) => {
    setSelectedMethod(method.id);
    // Select first available processor (for now, just Coinflow)
    onSelect(method.type, method.processors[0]);
  };

  const handleSavedCardSelect = (savedCard: any) => {
    if (onSelectSavedCard) {
      onSelectSavedCard(savedCard);
    }
  };

  // Check if user has saved cards - refresh when refreshKey changes
  useEffect(() => {
    const checkSavedCards = async () => {
      try {
        const response = await apiClient.get('/payments/methods');
        setHasSavedCards(response && response.length > 0);
      } catch {
        // If error, assume no saved cards
        setHasSavedCards(false);
      }
    };
    checkSavedCards();
  }, [refreshKey]); // Refresh when refreshKey changes

  if (loading) {
    return (
      <div className="space-y-2 lg:space-y-4">
        <div className="text-center py-4 lg:py-8 text-muted-foreground">
          <div className="animate-spin rounded-full h-5 w-5 lg:h-8 lg:w-8 border-2 border-primary border-t-transparent mx-auto mb-2"></div>
          <p className="text-[10px] lg:text-sm font-medium">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="space-y-2 lg:space-y-4">
        <div className="text-center py-4 lg:py-8 text-muted-foreground border border-dashed border-border rounded-lg">
          <p className="font-black text-[10px] lg:text-sm mb-1 lg:mb-2">No payment methods available</p>
          <p className="text-[9px] lg:text-sm">
            Please contact support or try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 lg:space-y-4">
      {/* Show saved cards if available */}
      {hasSavedCards && (
        <div className="space-y-2 lg:space-y-3">
          <h4 className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Saved Payment Methods</h4>
          <SavedPaymentMethods
            onSelect={handleSavedCardSelect}
            showSelectButton={true}
            refreshKey={refreshKey}
            showHeading={false}
          />
          <div className="pt-1.5 lg:pt-2 border-t border-border">
            <p className="text-[9px] lg:text-sm text-muted-foreground text-center">Or use a new payment method</p>
          </div>
        </div>
      )}

      {/* Available payment methods */}
      <div className={hasSavedCards ? 'space-y-2 lg:space-y-3' : ''}>
        {hasSavedCards && (
          <h4 className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Other Payment Methods</h4>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4">
          {/* Other payment methods as cards */}
          {methods.map((method) => (
            <Card
              key={method.id}
              className={cn(
                'cursor-pointer transition-all bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border hover:border-primary shadow-lg',
                selectedMethod === method.id && 'border-primary border-2 shadow-xl',
              )}
              onClick={() => handleMethodSelect(method)}
            >
              <CardContent className="p-2.5 lg:p-4">
                <div className="flex items-center gap-2 lg:gap-3">
                  <div className="text-muted-foreground flex-shrink-0">{method.icon}</div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-black text-[10px] lg:text-sm text-foreground truncate">{method.displayName}</h4>
                    <p className="text-[9px] lg:text-xs text-muted-foreground truncate">
                      {method.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Apple Pay and Google Pay buttons - separate section at bottom */}
      {/* Google Pay TEMPORARILY DISABLED - only show section if Apple Pay is available */}
      {(isApplePayMethodAvailable && isApplePayAvailable && onApplePaySuccess && onApplePayError) ? (
        <div className="pt-2 lg:pt-4 border-t border-border space-y-2 lg:space-y-3">
          <div
            className={cn(
              'grid gap-2 lg:gap-4',
              'grid-cols-1'
            )}
          >
            {/* Apple Pay button */}
            {isApplePayMethodAvailable && isApplePayAvailable && onApplePaySuccess && onApplePayError && (
              <div className="flex flex-col">
                <ApplePayButton
                  amount={amount}
                  packageId={packageId}
                  scheduledOfferId={scheduledOfferId}
                  packageTitle={packageTitle}
                  merchantId={import.meta.env.VITE_COINFLOW_MERCHANT_ID || ''}
                  onSuccess={onApplePaySuccess}
                  onError={onApplePayError}
                  checkoutSessionId={checkoutSessionId}
                />
              </div>
            )}

            {/* Google Pay button - TEMPORARILY DISABLED for testing */}
            {/* TODO: Re-enable once Coinflow Google Pay issue is resolved */}
            {false && isGooglePayMethodAvailable && isGooglePayAvailable && onGooglePaySuccess && onGooglePayError && (
              <div className="flex flex-col">
                <GooglePayButton
                  amount={amount}
                  packageId={packageId}
                  scheduledOfferId={scheduledOfferId}
                  packageTitle={packageTitle}
                  merchantId={import.meta.env.VITE_COINFLOW_MERCHANT_ID || ''}
                  onSuccess={onGooglePaySuccess}
                  onError={onGooglePayError}
                  checkoutSessionId={checkoutSessionId}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

